import {TooltipPrimitive as Tooltip} from "ch3-ui-lib";
import {PhoneHQIconAvatar, PhoneHQLoader, PhoneHQButton} from 'ch3-ui-lib';
import {ChevronRight, ListTree, Plus} from "lucide-react";
import {sortBy} from "lodash";
import {Link, useNavigate, useParams} from "react-router-dom";
import {type PhoneNumber, type QueueWithNumbers, useGetQueuesWithNumbersQuery} from "../../store/features/CallQueue/CallQueueService";
import {getFormatedPhoneNumber} from "../../utilis/msisdnUtilis";
import {SettingsLayout} from "../Settings/SettingsLayout";
import {QueueDrawer} from "./QueueDrawer";
import {strategyLabel} from "./queueModel";
import {lucide} from "./queueUi";

const MAX_TILES = 8;

/** Settings › Call queues (Queues-List, -ListHover, -Empty); /callQueues/:id opens the drawer over it. */
export const CallQueuesPage = () => {
    const {id} = useParams<{ id: string }>();
    const navigate = useNavigate();
    const {data, isLoading} = useGetQueuesWithNumbersQuery();
    const queues = sortBy(data ?? [], (q) => q.name.toLowerCase());

    const create = (
        <PhoneHQButton className="px-5" onClick={() => navigate('/callQueues/new')}>
            <Plus size={16} aria-hidden="true" className={lucide}/>
            Create call queue
        </PhoneHQButton>
    );

    return (
        <SettingsLayout
            title={
                <div className="flex items-baseline gap-3">
                    <h1 className="m-0 truncate text-headline text-ink">Call queues</h1>
                    {!isLoading && <span className="text-footnote text-ink-muted">{queues.length} {queues.length === 1 ? 'queue' : 'queues'}</span>}
                </div>
            }
            actions={create}
        >
            <div className="min-h-full bg-surface">
                {isLoading ? (
                    <div className="flex justify-center p-10"><PhoneHQLoader/></div>
                ) : queues.length === 0 ? (
                    <EmptyQueues/>
                ) : (
                    <Tooltip.Provider delayDuration={200}>
                        <div role="table" aria-label="Call queues">
                            <div role="row" className="flex h-9 items-center border-0 border-y border-solid border-line px-8 text-caption text-ink-muted">
                                <span role="columnheader" className="flex-[2]">Name</span>
                                <span role="columnheader" className="flex-[2]">Phone numbers</span>
                                <span role="columnheader" className="flex-[1.4]">Strategy</span>
                                <span role="columnheader" className="flex-1">Phone tree</span>
                                <span className="w-4" aria-hidden="true"/>
                            </div>
                            {queues.map((q) => <QueueRow key={q.id} queue={q}/>)}
                        </div>
                    </Tooltip.Provider>
                )}
            </div>
            {id && <QueueDrawer key={id} queueId={id} onClose={() => navigate('/callQueues')}/>}
        </SettingsLayout>
    );
};

const QueueRow = ({queue}: { queue: QueueWithNumbers }) => {
    const numbers = queue.phoneNumbers ?? [];
    return (
        <Link
            role="row"
            to={`/callQueues/${queue.id}`}
            className="flex h-14 items-center border-0 border-b border-solid border-line px-8 text-callout text-ink no-underline outline-none hover:bg-surface-muted focus-visible:bg-surface-muted focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-focus"
        >
            <span role="cell" className="min-w-0 flex-[2] truncate font-semibold">{queue.name}</span>
            <span role="cell" className="flex min-w-0 flex-[2] items-center gap-0.5">
                {numbers.length === 0 && <span className="text-ink-muted">No numbers</span>}
                {numbers.slice(0, MAX_TILES).map((n) => <NumberTile key={n.msisdnId ?? n.msisdn} number={n}/>)}
                {numbers.length > MAX_TILES && <span className="pl-1.5 text-footnote text-ink-muted">+{numbers.length - MAX_TILES}</span>}
            </span>
            <span role="cell" className="flex-[1.4]">{strategyLabel(queue.ringStrategy)}</span>
            <span role="cell" className="flex-1 text-ink-muted">{queue.ivrMenuOption?.enabled ? 'On' : 'Off'}</span>
            <ChevronRight size={16} aria-hidden="true" className={`${lucide} w-4 text-ink-muted`}/>
        </Link>
    );
};

/** Queues-ListHover: line tile with "Office · +48 667 887 975" on hover. */
const NumberTile = ({number}: { number: PhoneNumber }) => {
    const text = `${number.label} · ${getFormatedPhoneNumber(number.msisdn, true)}`;
    return (
        <Tooltip.Root>
            <Tooltip.Trigger asChild>
                <span tabIndex={0} aria-label={text} className="flex rounded-[6px] outline-none focus-visible:ring-2 focus-visible:ring-brand-focus">
                    <PhoneHQIconAvatar icon={number.icon || 'call'} color={number.iconColor ?? 'blue'} size="32px"/>
                </span>
            </Tooltip.Trigger>
            <Tooltip.Portal>
                <Tooltip.Content side="top" sideOffset={6} className="z-50 rounded-chip bg-zinc-950 px-2.5 py-1.5 text-footnote text-white shadow-menu">
                    {text}
                </Tooltip.Content>
            </Tooltip.Portal>
        </Tooltip.Root>
    );
};

/** Queues-Empty. */
const EmptyQueues = () => (
    <div className="flex flex-col items-center px-8 py-24 text-center">
        <span aria-hidden="true" className="mb-4 flex size-12 items-center justify-center rounded-[14px] bg-surface-muted text-ink-muted-strong">
            <ListTree size={22} className={lucide}/>
        </span>
        <h2 className="m-0 text-body-strong text-ink">No call queues yet</h2>
        <p className="m-0 mt-1 max-w-[360px] text-callout font-normal text-ink-muted">Queues route callers to the right team and play a menu while they wait.</p>
        <Link to="/callQueues/new" className="mt-5 inline-flex h-control-md items-center rounded-control bg-brand px-5 text-callout font-semibold text-white no-underline hover:bg-brand-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-focus focus-visible:ring-offset-2">
            Create call queue
        </Link>
    </div>
);
